import type { SubjectCode } from '~/models/Subject'
import type { YearPlan } from '~/models/Planner/YearPlan'
import type { PrePersistWeek, Week } from '~/models/Planner/Week'
import type { WeekResponse, YearPlanResponse } from '~/models/Planner/Response'
import type { FileForm, PageInfo, SignedFile } from '~/models/Planner/FileForm'
import type { Group } from '~/models/Group'
import type { AddToWeek, AddToWeekResponse } from '~/models/AddToWeek'
import { useSchoolYear } from '~/composables/useSchoolYear'
import usePlannerMapper from '~/composables/usePlannerMapper'
import { contentClient } from '~/api/client/contentClient'
import { apiClient } from '~/api/client/apiClient'

export function usePlannerApi() {
  const { mapWeek, mapYearPlan } = usePlannerMapper()

  const validateStatus = (status: number) => status >= 200 && status < 300

  const getYearPlans = async (
    schoolYear?: string,
    subjectCode?: string,
    weekNumber?: number,
  ): Promise<YearPlan[]> => {
    const { currentSchoolYearCode } = useSchoolYear()
    if (!schoolYear)
      schoolYear = currentSchoolYearCode
    const response = (await apiClient.get('planner/year_plans', {
      params: {
        ...(schoolYear ? { 'schoolYear.identifier': schoolYear } : {}),
        ...(subjectCode ? { 'subjects.identifier': subjectCode } : {}),
        ...(weekNumber ? { 'plans.weeks.weekNumber': weekNumber } : {}),
      },
      validateStatus,
    })).data['hydra:member'] ?? []
    return response.map(mapYearPlan)
  }

  const getYearPlan = async (yearPlan: string): Promise<YearPlan> => {
    const response = (await apiClient.get(`planner/year_plans/${yearPlan}`, { validateStatus })).data as YearPlanResponse
    return mapYearPlan(response)
  }

  const copyYearPlansToGroup = async (group: Group, identifiers: string[], weekNumber: number, subject?: SubjectCode): Promise<void> => {
    await apiClient.post(('planner/year_plans/copy_to_group'), { group, identifiers, weekNumber, subject }, { validateStatus })
  }

  const putWeek = async (week: Partial<PrePersistWeek>): Promise<Week> => {
    const response = (await apiClient.put(`planner/weeks/${week.identifier}`, week)).data as WeekResponse
    return mapWeek(response)
  }

  const deleteResource = (resourceIdentifier: string): Promise<void> => {
    return apiClient.delete(`planner/resources/${resourceIdentifier}`, { validateStatus })
  }

  const createFileForm = async (): Promise<FileForm> => {
    return (await apiClient.post('file/form', {})).data
  }

  const postSignedFile = async (key: string): Promise<SignedFile> => {
    return (await apiClient.post('file/sign', { key })).data
  }

  const crawlPageInfo = async (url: string): Promise<PageInfo> => {
    return (await contentClient.get(`/crawler/pageinfo?url=${encodeURIComponent(url)}`)).data
  }

  const postToWeek = async (addToWeek: AddToWeek): Promise<AddToWeekResponse> => {
    return (await apiClient.post('planner/addToWeek/resource', addToWeek)).data
  }

  const updateYearPlansByGroupId = (groupId: string): Promise<void> => {
    return apiClient.post('/planner/year_plans/groups/update/groupId', { groupId })
  }

  return {
    getYearPlans,
    copyYearPlansToGroup,
    deleteResource,
    getYearPlan,
    createFileForm,
    postSignedFile,
    crawlPageInfo,
    putWeek,
    postToWeek,
    updateYearPlansByGroupId,
  }
}
